import { useCallback, useState } from 'react';
import useFieldsCommentsApi from '../../api/comments/useFieldsCommentsApi';
import useFieldComments from '../../features/comments/useFieldComments';
import { makeFieldKey } from '../../../utils/formCrafter/listViewUtils';

/**
 * useListViewComments Hook
 *
 * Manages comment state and actions for FormCrafterListView.
 * Handles comment creation, modal states, and quick comment tracking.
 *
 * @param {Object} options - Hook options
 * @param {Array} options.comments - Initial comments array
 * @param {Function} options.setComments - Function to update comments
 * @param {string|number} options.formId - Form ID
 * @param {number} options.filledFormId - End user filled form ID
 * @param {boolean} options.enableAddComment - Whether adding comments is enabled
 * @param {number|string} requestId
 * @returns {Object} - Comment state and actions
 */
const useListViewComments = ({
  comments = [],
  setComments,
  formId,
  filledFormId,
  enableAddComment = false,
  requestId,
} = {}) => {
  const { addComment } = useFieldsCommentsApi();
  const { getFieldComments } = useFieldComments(comments);

  const [activeQuickCommentField, setActiveQuickCommentField] = useState(null);
  const [activeCommentsModalField, setActiveCommentsModalField] =
    useState(null);
  const [lastAddedComments, setLastAddedComments] = useState({});

  const getFieldKey = useCallback(
    (subformId, fieldId) => makeFieldKey(formId, subformId, fieldId),
    [formId]
  );

  const getCommentsForField = useCallback(
    (subformId, fieldId) => {
      if (!formId) return [];
      return getFieldComments(formId, subformId, fieldId);
    },
    [formId, getFieldComments]
  );

  const handleAddComment = useCallback(
    async ({ comment, subFormId, fieldId, attachedFiles }) => {
      if (!formId) {
        console.error('Cannot add comment: formId is required');
        throw new Error('Form ID is required to add comments');
      }

      try {
        const newComment = await addComment({
          comment,
          subFormId,
          fieldId,
          formId,
          endUserFilledFormId: filledFormId,
          requestId,
          attachedFiles,
        });

        if (setComments) {
          setComments((prev) => [...prev, newComment]);
        }

        return newComment;
      } catch (error) {
        console.error('Error adding comment:', error);
        throw error;
      }
    },
    [formId, filledFormId, addComment, setComments, requestId]
  );

  const handleCommentAdded = useCallback(
    (subformId, fieldId, newComment, fieldKeyOverride = null) => {
      const key = fieldKeyOverride || getFieldKey(subformId, fieldId);
      if (key && newComment) {
        setLastAddedComments((prev) => ({
          ...prev,
          [key]: newComment,
        }));
      }
    },
    [getFieldKey]
  );

  const handleQuickCommentToggle = useCallback((fieldKey, isOpen) => {
    setActiveQuickCommentField(isOpen ? fieldKey : null);
  }, []);

  const handleCommentsModalToggle = useCallback((fieldKey, isOpen) => {
    setActiveCommentsModalField(isOpen ? fieldKey : null);
  }, []);

  const closeQuickComment = useCallback(() => {
    setActiveQuickCommentField(null);
  }, []);

  const openCommentsModal = useCallback((fieldKey) => {
    setActiveQuickCommentField(null);
    setActiveCommentsModalField(fieldKey);
  }, []);

  return {
    getFieldKey,
    getCommentsForField,
    handleAddComment,
    handleCommentAdded,
    activeQuickCommentField,
    activeCommentsModalField,
    lastAddedComments,
    handleQuickCommentToggle,
    handleCommentsModalToggle,
    closeQuickComment,
    openCommentsModal,
    enableAddComment,
  };
};

export default useListViewComments;
